import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { SOFTWARE_ROLES, canAccessPath, filterItemsByAccess, getCurrentAppUser, normalizeSoftwareRole } from '../utils/accessControl';
import './MainContent.css';

function MainContent({ onLogout }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentUser = getCurrentAppUser();
  const softwareRole = normalizeSoftwareRole(currentUser?.softwareRole);
  const [leanOpen, setLeanOpen] = useState(false);
  const [jobCards, setJobCards] = useState([]);
  const [jobCardsError, setJobCardsError] = useState('');
  const [isLoadingJobCards, setIsLoadingJobCards] = useState(false);
  const [partIssueActivities, setPartIssueActivities] = useState([]);
  const [estimateApprovalActivities, setEstimateApprovalActivities] = useState([]);
  const [expenseActivities, setExpenseActivities] = useState([]);
  const [activityError, setActivityError] = useState('');
  const [activeGatePasses, setActiveGatePasses] = useState([]);
  const [selectedJobStatus, setSelectedJobStatus] = useState('Created');
  const [jobCardSearch, setJobCardSearch] = useState('');
  const jobStatusFilters = [
    { label: t('dashboard.create'), value: 'Created' },
    { label: t('dashboard.wip'), value: 'WIP' },
    { label: t('dashboard.waitParts'), value: 'Waiting Parts' },
    { label: t('dashboard.completed'), value: 'Completed' },
  ];

  const modules = useMemo(() => ([
    { id: 1, icon: '\u{1F465}', name: t('dashboard.clients'), path: '/clients' },
    { id: 2, icon: '\u{1F697}', name: t('dashboard.vehicles'), path: '/vehicles' },
    { id: 3, icon: '\u{1F4E6}', name: t('dashboard.inventory'), path: '/inventory' },
    { id: 4, icon: '\u{1F91D}', name: t('dashboard.vendors'), path: '/vendors' },
    { id: 5, icon: '\u{1F4CA}', name: t('dashboard.accounting'), path: '/accounting' },
    { id: 6, icon: '\u{1F4CB}', name: t('dashboard.jobCards'), path: '/jobcards' },
    { id: 7, icon: '\u{1F4C4}', name: t('dashboard.invEst'), path: '/invoices' },
    { id: 8, icon: '\u25A6', name: t('dashboard.bayBoard'), path: '/bays' },
    { id: 9, icon: '\u2713', name: t('dashboard.inspections'), path: '/inspection' },
    { id: 10, icon: '\u{1F4C5}', name: t('dashboard.appointments'), path: '/appointments' },
    { id: 11, icon: '\u{1F514}', name: t('dashboard.serviceReminders'), path: '/service-reminders' },
    { id: 13, icon: '\u{1F48A}', name: 'Diagnose' },
    { id: 14, icon: '\u{1F4D6}', name: t('dashboard.laborGuides'), path: '/hr' },
    { id: 15, icon: '\u23F1\uFE0F', name: t('dashboard.timeTracking'), path: '/time-tracking' },
    { id: 16, icon: '\u{1F3E6}', name: t('dashboard.estimate'), path: '/estimates' },
    { id: 17, icon: '\u{1F4E2}', name: 'Marketing' },
    { id: 18, icon: '\u2699', name: 'Lean System Management' },
    { id: 19, icon: '\u{1F4B8}', name: 'Expense', path: '/expenses' },
  ]), [t]);
  const canViewJobCards = canAccessPath('/jobcards', currentUser);
  const canViewEstimates = canAccessPath('/estimates', currentUser);
  const canViewInventoryIssue = canAccessPath('/inventory/issue-parts', currentUser);
  const canViewExpenses = canAccessPath('/expenses', currentUser) || softwareRole === SOFTWARE_ROLES.ADMIN;
  const canViewExpenseActivities = canViewExpenses;
  const canViewActivity = canViewJobCards || canViewEstimates || canViewInventoryIssue || canViewExpenseActivities;
  const visibleModules = useMemo(() => {
    const baseModules = filterItemsByAccess(modules.filter((module) => module.path), 'path', currentUser);
    const extraModules = modules.filter((module) => !module.path).filter((module) => {
      if (module.id === 13) {
        return [SOFTWARE_ROLES.OPERATION_HEAD, SOFTWARE_ROLES.INSPECTIONS_DEPT, SOFTWARE_ROLES.ADMIN].includes(softwareRole);
      }

      if (module.id === 18) {
        return softwareRole === SOFTWARE_ROLES.ADMIN;
      }

      return softwareRole === SOFTWARE_ROLES.ADMIN;
    });

    return [...baseModules, ...extraModules];
  }, [currentUser, modules, softwareRole]);

  const leanSections = [
    {
      title: 'Core Philosophy',
      items: [
        { title: 'Kaizen', detail: 'Continuous Improvement' },
        { title: 'Respect for People' },
        { title: 'Long-term Thinking' },
      ],
    },
    {
      title: '3M Problems (Main Loss Sources)',
      items: [
        { title: 'Muda', detail: 'Waste' },
        { title: 'Muri', detail: 'Overburden' },
        { title: 'Mura', detail: 'Unevenness' },
      ],
    },
    {
      title: 'Waste Reduction & Flow',
      items: [
        { title: 'JIT', detail: 'Just-In-Time' },
        { title: 'Kanban' },
        { title: 'Heijunka' },
        { title: 'Takt Time' },
        { title: 'One-piece Flow' },
      ],
    },
    {
      title: 'Quality Improvement',
      items: [
        { title: 'Jidoka' },
        { title: 'Poka-Yoke' },
        { title: 'Andon' },
        { title: 'Root Cause Analysis' },
        { title: '5 Why' },
      ],
    },
    {
      title: 'Workplace Organization',
      items: [
        {
          title: '5S',
          children: [
            { title: 'Seiri', detail: 'Sort' },
            { title: 'Seiton', detail: 'Set in Order' },
            { title: 'Seiso', detail: 'Shine' },
            { title: 'Seiketsu', detail: 'Standardize' },
            { title: 'Shitsuke', detail: 'Sustain' },
          ],
        },
        { title: 'Visual Management' },
      ],
    },
    {
      title: 'Workplace Culture & Observation',
      items: [
        { title: 'Gemba' },
        { title: 'Genchi Genbutsu' },
        { title: 'Teamwork Culture' },
      ],
    },
    {
      title: 'Standardization',
      items: [
        { title: 'Standard Work' },
        { title: 'SOP' },
        { title: 'Continuous Process Control' },
      ],
    },
    {
      title: 'Strategic Management',
      items: [
        { title: 'Hoshin Kanri' },
        {
          title: 'PDCA Cycle',
          children: [
            { title: 'Plan' },
            { title: 'Do' },
            { title: 'Check' },
            { title: 'Act' },
          ],
        },
        { title: 'Lean Leadership' },
      ],
    },
  ];

  const fetchActiveJobCards = useCallback(async () => {
    setIsLoadingJobCards(true);
    setJobCardsError('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/jobcards', {
        params: {
          archivedOnly: false,
          documentType: 'JobCard',
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setJobCards(
        (response.data.jobCards || []).filter((jobCard) => jobCard.Status !== 'Closed')
      );
    } catch (err) {
      setJobCardsError(err.response?.data?.message || 'Failed to load active job cards.');
    } finally {
      setIsLoadingJobCards(false);
    }
  }, []);

  const fetchPartIssueActivities = useCallback(async () => {
    setActivityError('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/jobcards/part-issue-activities', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setPartIssueActivities(response.data.activities || []);
    } catch (err) {
      setActivityError(err.response?.data?.message || 'Failed to load activity.');
    }
  }, []);

  const fetchEstimateApprovalActivities = useCallback(async () => {
    setActivityError('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/jobcards/estimate-approval-activities', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setEstimateApprovalActivities(response.data.activities || []);
    } catch (err) {
      setActivityError(err.response?.data?.message || 'Failed to load activity.');
    }
  }, []);

  const fetchExpenseActivities = useCallback(async () => {
    setActivityError('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/expenses/activities', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setExpenseActivities(response.data.activities || []);
    } catch (err) {
      setActivityError(err.response?.data?.message || 'Failed to load activity.');
    }
  }, []);

  const fetchActiveGatePasses = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/gatepass/active', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setActiveGatePasses(response.data.gatePasses);
      }
    } catch (err) {
      console.error('Failed to load gate passes', err);
    }
  }, []);

  const handleDeliverGatePass = async (gatePass) => {
    if (window.confirm(`Are you sure and OK to deliver car ${gatePass.car_number}?`)) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.post(`/api/gatepass/deliver/${gatePass.id}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.success) {
          setActiveGatePasses(prev => prev.filter(gp => gp.id !== gatePass.id));
        }
      } catch (err) {
        console.error('Failed to deliver gate pass', err);
        alert('Failed to update gate pass status');
      }
    }
  };

  const clearPartIssueActivities = async () => {
    setActivityError('');

    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/jobcards/part-issue-activities/clear', {}, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setPartIssueActivities([]);
    } catch (err) {
      setActivityError(err.response?.data?.message || 'Failed to clear activity.');
    }
  };

  const clearEstimateApprovalActivities = async () => {
    setActivityError('');

    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/jobcards/estimate-approval-activities/clear', {}, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setEstimateApprovalActivities([]);
    } catch (err) {
      setActivityError(err.response?.data?.message || 'Failed to clear activity.');
    }
  };

  const dismissPartIssueActivity = async (requestId) => {
    setActivityError('');

    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/jobcards/part-issue-activities/${requestId}/dismiss`, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setPartIssueActivities((current) => current.filter((item) => item.PartIssueRequestId !== requestId));
    } catch (err) {
      setActivityError(err.response?.data?.message || 'Failed to dismiss activity.');
    }
  };

  const dismissEstimateApprovalActivity = async (jobCardId) => {
    setActivityError('');

    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/jobcards/estimate-approval-activities/${jobCardId}/dismiss`, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setEstimateApprovalActivities((current) => current.filter((item) => item.JobCardId !== jobCardId));
    } catch (err) {
      setActivityError(err.response?.data?.message || 'Failed to dismiss activity.');
    }
  };

  const dismissExpenseActivity = async (expenseId) => {
    setActivityError('');
    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/expenses/activities/${expenseId}/dismiss`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setExpenseActivities((current) => current.filter((item) => item.ExpenseId !== expenseId));
    } catch (err) {
      setActivityError(err.response?.data?.message || 'Failed to dismiss activity.');
    }
  };

  useEffect(() => {
    if (canViewJobCards) {
      fetchActiveJobCards();
    }

    if (canViewJobCards || canViewInventoryIssue) {
      fetchPartIssueActivities();
    }

    if (canViewEstimates) {
      fetchEstimateApprovalActivities();
    }
    
    if (canViewExpenseActivities) {
      fetchExpenseActivities();
    }
    
    fetchActiveGatePasses();
  }, [canViewEstimates, canViewInventoryIssue, canViewJobCards, canViewExpenseActivities, fetchActiveJobCards, fetchEstimateApprovalActivities, fetchPartIssueActivities, fetchActiveGatePasses, fetchExpenseActivities]);

  const totalActivityCount = partIssueActivities.length + estimateApprovalActivities.length + expenseActivities.length;

  const visibleJobCards = useMemo(() => {
    const searchValue = jobCardSearch.trim().toLowerCase();

    return jobCards
      .filter((jobCard) => String(jobCard.Status || 'Created').toLowerCase() === selectedJobStatus.toLowerCase())
      .filter((jobCard) => {
        if (!searchValue) {
          return true;
        }

        return String(jobCard.JobNumber || jobCard.JobCardId || '').toLowerCase().includes(searchValue);
      })
      .slice(0, 5);
  }, [jobCardSearch, jobCards, selectedJobStatus]);

  const formatDateTime = (dateValue) => {
    if (!dateValue) {
      return '-';
    }

    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }).format(new Date(dateValue)).replace(',', '');
  };

  const formatMoney = (amount) => `Tk${Number(amount || 0).toFixed(0)}`;

  const getStatusClassName = (status) => String(status || 'created').toLowerCase().replace(/\s+/g, '-');

  const openPartIssueActivity = async (activity) => {
    const nextPath = ['Pending', 'Return Pending'].includes(activity.Status)
      ? `/inventory/issue-parts?requestId=${activity.PartIssueRequestId}&jobCardId=${activity.JobCardId}`
      : `/jobcards/${activity.JobCardId}/edit`;
    // Do not auto-dismiss the activity when navigating into the Issue Parts
    // page. Keep the notification active until the delivery/accept action
    // happens on the IssueParts page so inventory staff can complete delivery.
    navigate(nextPath);
  };

  const openEstimateApprovalActivity = async (activity) => {
    setEstimateApprovalActivities((current) => current.filter((item) => item.JobCardId !== activity.JobCardId));

    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/jobcards/estimate-approval-activities/${activity.JobCardId}/dismiss`, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (err) {
      setActivityError(err.response?.data?.message || 'Failed to dismiss activity.');
    } finally {
      navigate(`/estimates/${activity.JobCardId}/edit`);
    }
  };

  const refreshActivities = async () => {
    const tasks = [];
    if (canViewJobCards || canViewInventoryIssue) {
      tasks.push(fetchPartIssueActivities());
    }
    if (canViewEstimates) {
      tasks.push(fetchEstimateApprovalActivities());
    }
    if (canViewExpenseActivities) {
      tasks.push(fetchExpenseActivities());
    }
    await Promise.all(tasks);
  };

  const clearAllActivities = async () => {
    const tasks = [];
    if (canViewJobCards || canViewInventoryIssue) {
      tasks.push(clearPartIssueActivities());
    }
    if (canViewEstimates) {
      tasks.push(clearEstimateApprovalActivities());
    }
    await Promise.all(tasks);
  };

  const openModule = (module) => {
    if (module.id === 18) {
      setLeanOpen((current) => !current);
      return;
    }

    if (!module.path) {
      return;
    }

    if (module.path === '/hr') {
      navigate('/hr', {
        state: { refreshAt: Date.now() },
      });
      return;
    }

    navigate(module.path);
  };

  return (
    <div className="main-content">
      <header className="content-header">
        <div className="header-top">
          <h1>Home</h1>
          <div className="header-actions">
            {canViewActivity && (
              <button className="btn-activity" onClick={refreshActivities}>
                Activity {totalActivityCount ? `(${totalActivityCount})` : ''}
              </button>
            )}
            {softwareRole !== SOFTWARE_ROLES.GUARD && (
              <button className="btn-portal">Portal Chats</button>
            )}
            {onLogout && (
              <button className="btn-logout" onClick={onLogout} title="Logout">
                Logout
              </button>
            )}
          </div>
        </div>
      </header>

      {softwareRole !== SOFTWARE_ROLES.GUARD && (
        <div className="modules-grid">
          {visibleModules.map((module) => (
            <div
              key={module.id}
              className={`module-card${window.location.pathname === module.path || (module.id === 18 && leanOpen) ? ' active-module' : ''}${module.id === 18 ? ' module-card-lean' : ''}`}
              onClick={() => openModule(module)}
              style={{ cursor: module.path || module.id === 18 ? 'pointer' : 'default' }}
            >
              <div className="module-icon">{module.icon}</div>
              <div className="module-name">{module.name}</div>
            </div>
          ))}
        </div>
      )}

      {(activeGatePasses.length > 0 || softwareRole === SOFTWARE_ROLES.GUARD) && (
        <section className="gate-pass-panel" style={{ marginTop: '20px', padding: '15px', backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <div className="section-header">
            <h2>Active Gate-Pass (Delivery Cars)</h2>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '10px' }}>
            {activeGatePasses.length === 0 ? (
              <p>No active gate passes right now.</p>
            ) : (
              activeGatePasses.map(gatePass => (
                <button
                  key={gatePass.id}
                  onClick={() => handleDeliverGatePass(gatePass)}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#4caf50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                  }}
                >
                  <span>Car: {gatePass.car_number || 'Unknown'}</span>
                  <span style={{ fontSize: '12px', marginTop: '5px' }}>Job: {gatePass.jobcard_number}</span>
                </button>
              ))
            )}
          </div>
        </section>
      )}

      {leanOpen && (
        <section className="lean-panel">
          <div className="section-header lean-panel-header">
            <div className="section-title-group lean-title-group">
              <span className="lean-kicker">Continuous Improvement Framework</span>
              <h2>Lean System Management</h2>
              <p>
                A practical management map for waste reduction, quality, culture, standard work, and strategic execution.
              </p>
            </div>
            <button className="btn-view-all lean-hide-button" type="button" onClick={() => setLeanOpen(false)}>
              Hide {'\u2191'}
            </button>
          </div>

          <div className="lean-chart-grid">
            {leanSections.map((section, index) => (
              <article key={section.title} className={`lean-card lean-card-tone-${index % 8}`}>
                <h3>{section.title}</h3>
                <div className="lean-list">
                  {section.items.map((item) => (
                    <div key={item.title} className="lean-item">
                      <div className="lean-item-title">
                        <strong>{item.title}</strong>
                        {item.detail ? <span>= {item.detail}</span> : null}
                      </div>
                      {item.children?.length ? (
                        <div className="lean-children">
                          {item.children.map((child) => (
                            <div key={child.title} className="lean-child">
                              <strong>{child.title}</strong>
                              {child.detail ? <span>= {child.detail}</span> : null}
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {canViewActivity && (partIssueActivities.length > 0 || estimateApprovalActivities.length > 0 || expenseActivities.length > 0 || activityError) && (
        <section className="activity-panel">
          <div className="section-header">
            <div className="section-title-group">
              <h2>Activity</h2>
              <div className="section-actions">
                <button className="btn-refresh" type="button" onClick={refreshActivities}>
                  Refresh {'\u21BB'}
                </button>
                {totalActivityCount > 0 && (
                  <button className="btn-clear-activity" type="button" onClick={clearAllActivities}>
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>

          {activityError && <div className="active-jobs-error">{activityError}</div>}

          <div className="activity-list">
            {partIssueActivities.map((activity) => (
              <div
                key={activity.PartIssueRequestId}
                className="activity-item"
              >
                <button
                  type="button"
                  className="activity-item-main"
                  onClick={() => openPartIssueActivity(activity)}
                >
                  <span className={`activity-status activity-status-${String(activity.Status || '').toLowerCase().replace(/\s+/g, '-')}`}>
                    {activity.Status}
                  </span>
                  <strong>
                    {activity.Status === 'Return Pending'
                      ? `Job Card #${activity.JobNumber || activity.JobCardId} rejected part waiting inventory acceptance`
                      : `Job Card #${activity.JobNumber || activity.JobCardId} requested parts issue`}
                  </strong>
                  <small>
                    {activity.ItemCount || 0} item{Number(activity.ItemCount || 0) === 1 ? '' : 's'}
                    {activity.ClientName ? ` | ${activity.ClientName}` : ''}
                    {activity.CarNumber ? ` | ${activity.CarNumber}` : ''}
                  </small>
                </button>
                <button
                  type="button"
                  className="activity-item-dismiss"
                  onClick={() => dismissPartIssueActivity(activity.PartIssueRequestId)}
                  aria-label={`Clear activity for job card ${activity.JobNumber || activity.JobCardId}`}
                  title="Clear this activity"
                >
                  {'\u00D7'}
                </button>
              </div>
            ))}
            {estimateApprovalActivities.map((activity) => (
              <div
                key={`estimate-${activity.JobCardId}`}
                className="activity-item"
              >
                <button
                  type="button"
                  className="activity-item-main"
                  onClick={() => openEstimateApprovalActivity(activity)}
                >
                  <span className="activity-status activity-status-approved">
                    Approved
                  </span>
                  <strong>
                    Estimate #{activity.JobNumber || activity.JobCardId} approved from client app
                  </strong>
                  <small>
                    {activity.ClientName ? `${activity.ClientName}` : 'Unknown client'}
                    {activity.CarNumber ? ` | ${activity.CarNumber}` : ''}
                    {activity.ClientApprovedAt ? ` | ${formatDateTime(activity.ClientApprovedAt)}` : ''}
                  </small>
                </button>
                <button
                  type="button"
                  className="activity-item-dismiss"
                  onClick={() => dismissEstimateApprovalActivity(activity.JobCardId)}
                  aria-label={`Clear estimate approval activity for estimate ${activity.JobNumber || activity.JobCardId}`}
                  title="Clear this activity"
                >
                  {'\u00D7'}
                </button>
              </div>
            ))}
                {expenseActivities.map((activity) => (
                  <div
                    key={`expense-${activity.ExpenseId}`}
                    className="activity-item"
                  >
                <button
                  type="button"
                  className="activity-item-main"
                  onClick={() => navigate('/expenses')}
                >
                  <span className={`activity-status activity-status-${String(activity.Status || 'pending').toLowerCase().replace(/\s+/g, '-')}`}>
                    {activity.Status || 'Pending'}
                  </span>
                  <strong>
                    {String(activity.Status || '').toLowerCase() === 'approved'
                      ? `Expense bill approved for payment: ${activity.EmployeeName}`
                      : `Expense bill submitted by ${activity.EmployeeName}`}
                  </strong>
                  <small>
                    {activity.ExpenseHead} | {formatMoney(activity.Amount)}
                    {(activity.ApprovedAt || activity.CreatedAt) ? ` | ${formatDateTime(activity.ApprovedAt || activity.CreatedAt)}` : ''}
                  </small>
                </button>
                <button
                  type="button"
                  className="activity-item-dismiss"
                  onClick={() => dismissExpenseActivity(activity.ExpenseId)}
                  aria-label={`Clear expense activity for expense ${activity.ExpenseId}`}
                  title="Clear this activity"
                >
                  {'\u00D7'}
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {canViewJobCards && (
      <section className="active-jobs">
        <div className="section-header">
          <div className="section-title-group">
            <h2>[{visibleJobCards.length}]</h2>
            <h2>Active Job Cards</h2>
            <button className="btn-refresh" type="button" onClick={fetchActiveJobCards} disabled={isLoadingJobCards}>
              {isLoadingJobCards ? 'Refreshing...' : 'Refresh'} {'\u21BB'}
            </button>
          </div>
          <button className="btn-view-all" type="button" onClick={() => navigate('/jobcards')}>
            View all {'\u2192'}
          </button>
        </div>

        <div className="active-job-controls">
          <div className="active-job-filter-list" role="tablist" aria-label="Active job card status">
            {jobStatusFilters.map((status) => (
              <button
                key={status.value}
                type="button"
                className={selectedJobStatus === status.value ? 'active' : ''}
                onClick={() => setSelectedJobStatus(status.value)}
              >
                {status.label}
              </button>
            ))}
          </div>
          <label className="active-job-search">
            <span aria-hidden="true">{'\u2315'}</span>
            <input
              type="search"
              placeholder="Search job card number"
              value={jobCardSearch}
              onChange={(event) => setJobCardSearch(event.target.value)}
            />
          </label>
        </div>

        {jobCardsError && <div className="active-jobs-error">{jobCardsError}</div>}

        {visibleJobCards.length > 0 ? (
          <div className="active-job-list">
            {visibleJobCards.map((jobCard) => {
              const vehicle = [jobCard.Year, jobCard.Make, jobCard.Model].filter(Boolean).join(' ');
              const statusClassName = getStatusClassName(jobCard.Status);

              return (
                <article
                  key={jobCard.JobCardId}
                  className="active-job-card"
                  onClick={() => navigate(`/jobcards/${jobCard.JobCardId}/edit`)}
                >
                  <div className={`active-job-status active-job-status-${statusClassName}`}>
                    <span aria-hidden="true">{jobCard.Status === 'WIP' ? '\u2699' : '\u21E9'}</span>
                    <strong>{jobCard.Status || 'Created'}</strong>
                  </div>

                  <div className="active-job-main">
                    <div className="active-job-topline">
                      <div>
                        <h3>{jobCard.ClientName || 'Unknown Client'}</h3>
                        <span>Invoice #{jobCard.JobNumber || jobCard.JobCardId}</span>
                        <time>{formatDateTime(jobCard.CreatedDate)}</time>
                      </div>
                      <button
                        type="button"
                        className="active-job-open"
                        onClick={(event) => {
                          event.stopPropagation();
                          navigate(`/jobcards/${jobCard.JobCardId}/edit`);
                        }}
                      >
                        {jobCard.Status === 'Completed' ? 'View' : 'Open'}
                      </button>
                    </div>

                    <div className="active-job-vehicle">
                      <span aria-hidden="true">{'\u{1F697}'}</span>
                      <strong>{vehicle || 'Vehicle not set'}</strong>
                      {jobCard.CarNumber && <em>| {jobCard.CarNumber}</em>}
                    </div>

                    <div className="active-job-totals">
                      <span>Total {formatMoney(jobCard.TotalAmount)}</span>
                      <span>Paid: {formatMoney(jobCard.PaidAmount)}</span>
                      <span>Due {formatMoney(jobCard.BalanceDue)}</span>
                    </div>
                  </div>

                  <span className="active-job-chevron" aria-hidden="true">{'\u203A'}</span>
                </article>
              );
            })}
          </div>
        ) : !jobCardsError && (
          <div className="no-jobs">
          <div className="no-jobs-icon">{'\u2B24'}</div>
          <p className="no-jobs-title">No {selectedJobStatus} Job Cards</p>
          <p className="no-jobs-desc">
            {jobCardSearch.trim()
              ? 'No job cards match that job card number.'
              : 'There are no job cards in this status right now.'}
          </p>
          </div>
        )}
      </section>
      )}
    </div>
  );
}

export default MainContent;
